/**
 * =========================================================================
 * SIDEBAR NAVIGATION COMPONENT
 * =========================================================================
 * 
 * This component provides the main navigation sidebar for authenticated users.
 * It displays user information, navigation links, and quick actions.
 * 
 * Key Features:
 * - User profile display with avatar and info
 * - Role-based navigation menu rendering
 * - Notification and messaging quick actions
 * - Responsive design with hover effects
 * - Clean logout functionality
 * 
 * Auth Integration:
 * - Uses auth context for user data
 * - Conditional rendering based on user type
 * - Proper loading states during auth checks
 * 
 * Navigation Structure:
 * - Logo section
 * - User profile section
 * - Quick actions (notifications, chat)
 * - Role-based navigation menu
 * - Logout section
 * =========================================================================
 */

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { useAuth } from "../contexts/authContext";
import { useLanguage } from "../contexts/languageContext";
import { useParent } from "../contexts/parentContext";
import { useSignOut } from "../hooks/useAuth";
import logo from "../assets/images/logo.png";
import organizationNav from "./navs/organizationNav";
import playerNav from "./navs/playerNav";
import talentScoutNav from "./navs/talentScoutNav";
import supervisorNav from "./navs/supervisorNav";
import parentNav, { getParentNav } from "./navs/parentNav";
import DOMPurify from "dompurify";
import ProfileImage from "../components/profileImage";
// import NotificationSection from "./navs/notificationSection"; // Commented out - file was deleted
import Loading from "./loading";

/**
 * Aside Component
 * 
 * Main sidebar navigation component that adapts to user roles and permissions.
 * Provides comprehensive navigation and user management features.
 * 
 * Features:
 * - Dynamic user profile display
 * - Role-based navigation menus
 * - Notification and chat quick access
 * - Secure logout functionality
 * - Responsive design patterns
 * 
 * User Types Supported:
 * - organization: Full organization management features
 * - player: Player-specific features (team-dependent)
 * - talent-scout: Scouting and analysis tools
 * - supervisor: Organization management access
 */
export default function Aside({ onClose, isMobile = false }) {
  // Get translation and language context
  const { t } = useTranslation();
  const { isRTL, getDirectionClass } = useLanguage();
  
  // Get user data and authentication state from context
  const { user, loading } = useAuth();
  const { controlMode, exitControlMode } = useParent();
  const signOutMutation = useSignOut();

  // State for reactive notification count
  const [unreadNotifications, setUnreadNotifications] = useState(() => {
    const stored = localStorage.getItem('unreadNotifications');
    return stored ? parseInt(stored) : 0;
  });

  // State for reactive message count
  const [unreadMessages, setUnreadMessages] = useState(() => {
    const stored = localStorage.getItem('unreadMessages');
    return stored ? parseInt(stored) : 0;
  });

  // Listen for notification count updates from socket
  useEffect(() => {
    const handleUnreadCountUpdate = (event) => {
      const newCount = event.detail.unreadNotifications;
      const finalCount = newCount || 0;
      setUnreadNotifications(finalCount);
      
      // Also update localStorage in case it's out of sync
      localStorage.setItem('unreadNotifications', finalCount.toString());
    };

    const handleUnreadMessagesUpdate = (event) => {
      const newCount = event.detail.unreadMessages;
      const finalCount = newCount || 0;
      setUnreadMessages(finalCount);
      
      // Also update localStorage in case it's out of sync
      localStorage.setItem('unreadMessages', finalCount.toString());
    };

    const handleNewNotification = (event) => {
      // The unreadCountUpdate event should handle the count update
      // This is just for potential future features
    };

    // Add event listeners for socket updates
    window.addEventListener('unreadCountUpdate', handleUnreadCountUpdate);
    window.addEventListener('unreadMessagesUpdate', handleUnreadMessagesUpdate);
    window.addEventListener('newNotification', handleNewNotification);

    // Also listen for storage changes (in case another tab updates it)
    const handleStorageChange = (event) => {
      if (event.key === 'unreadNotifications') {
        const newCount = parseInt(event.newValue) || 0;
        setUnreadNotifications(newCount);
      } else if (event.key === 'unreadMessages') {
        const newCount = parseInt(event.newValue) || 0;
        setUnreadMessages(newCount);
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Cleanup listeners
    return () => {
      window.removeEventListener('unreadCountUpdate', handleUnreadCountUpdate);
      window.removeEventListener('unreadMessagesUpdate', handleUnreadMessagesUpdate);
      window.removeEventListener('newNotification', handleNewNotification);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [unreadNotifications, unreadMessages]);

  // Periodic sync with localStorage (fallback for any missed events)
  useEffect(() => {
    const syncInterval = setInterval(() => {
      const storedNotifications = parseInt(localStorage.getItem('unreadNotifications')) || 0;
      const storedMessages = parseInt(localStorage.getItem('unreadMessages')) || 0;
      
      if (storedNotifications !== unreadNotifications) {
        setUnreadNotifications(storedNotifications);
      }
      
      if (storedMessages !== unreadMessages) {
        setUnreadMessages(storedMessages);
      }
    }, 5000); // Check every 5 seconds

    return () => {
      clearInterval(syncInterval);
    };
  }, [unreadNotifications, unreadMessages]);

  // Show loading state while user data is being fetched
  if (loading || !user) {
    return (
      <aside className={`h-full ${isMobile ? 'w-full' : 'w-72'} flex flex-col bg-white shadow-xl border-r border-gray-100 ${isMobile ? 'rounded-r-3xl' : 'rounded-3xl'}`}>
        <div className="flex items-center justify-center h-full">
          <Loading />
        </div>
      </aside>
    );
  }

  const userType = user.accountType;

  /**
   * Handle sign out
   */
  const handleSignOut = async () => {
    try {
      await signOutMutation.mutateAsync();
    } catch (error) {
      console.error('Sign out failed:', error);
    }
  };

  /**
   * Create safe HTML for SVG icons
   * Sanitizes SVG content to prevent XSS attacks while preserving functionality
   */
  const createSafeHtml = (htmlContent) => {
    return {
      __html: DOMPurify.sanitize(htmlContent, {
        USE_PROFILES: { svg: true, svgFilters: true },
        ADD_ATTR: ["viewBox"], // Allow viewBox attribute for proper SVG scaling
      }),
    };
  };

  /**
   * Get appropriate navigation items based on user type
   * Returns the correct navigation array for the current user's role
   */
  const getNavigationItems = () => {
    switch (userType) {
      case "organization":
        return organizationNav;
      case "supervisor":
        // Filter supervisor navigation based on roles
        return supervisorNav.filter(item => {
          // If no permissions required, always show
          if (!item.permissions || item.permissions.length === 0) {
            return true;
          }
          
          // Check permissions based on the actual user data structure
          return item.permissions.some(permission => {
            switch (permission) {
              case 'manage_members':
              case 'view_members':
                // Check if user has supervisor read/update permissions
                return user?.metaData?.roles?.supervisor?.read || user?.metaData?.roles?.supervisor?.update;
              case 'view_reports':
                // Check if user has reports read permission
                return user?.metaData?.roles?.reports?.read || user?.metaData?.roles?.reports?.export;
              case 'manage_settings':
                // Check if user has supervisor update permission
                return user?.metaData?.roles?.supervisor?.update;
              default:
                return false;
            }
          });
        });
      case "player":
        // Filter player navigation based on verification status
        return playerNav.filter(item => {
          // If no verification required, always show
          if (!item.requiresVerification) {
            return true;
          }
          
          // Check if player is verified
          return user?.verifiedPlayer === true;
        });
      case "talent-scout":
        return talentScoutNav;
      case "parent":
        return getParentNav(controlMode);
      default:
        return [];
    }
  };

  const navigationItems = getNavigationItems();

  return (
    <aside className={`h-full ${isMobile ? 'w-full' : 'w-72'} flex flex-col bg-white shadow-xl border-gray-100 ${isMobile ? 'rounded-r-3xl' : 'rounded-3xl'} ${getDirectionClass()} ${isRTL ? 'border-l' : 'border-r'}`}>
      
      {/* ==================== MOBILE HEADER (close button) ==================== */}
      {isMobile && (
        <div className="flex items-center justify-between p-4 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-800">Menu</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close menu"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      {/* ==================== LOGO SECTION ==================== */}
      <div className={`${isMobile ? 'p-4' : 'p-6'} border-b border-gray-100`}>
        <Link 
          to={userType === "organization" || userType === "supervisor" ? "/organization/dashboard" : "/feed"}
          className="block"
          onClick={isMobile ? onClose : undefined}
        >
        <img
          src={logo}
          alt="Application Logo"
          className={`${isMobile ? 'w-32' : 'w-[160px]'} mx-auto hover:opacity-90 transition-opacity cursor-pointer`}
        />
        </Link>
      </div>

      {/* ==================== USER PROFILE SECTION ==================== */}
      <Link
        to="/user/profile"
        className={`${isMobile ? 'p-4' : 'p-5'} border-b border-gray-100 hover:bg-gray-50/80 transition-colors group`}
        aria-label={`View profile for ${user.userName}`}
        onClick={isMobile ? onClose : undefined}
      >
        <div className={`flex items-center ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}>
          <ProfileImage
            className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} rounded-full overflow-hidden ring-2 ring-gray-100 group-hover:ring-blue-100 transition-all`}
            alt={`${user.userName}'s profile picture`}
          />
          <div className="flex-1 min-w-0">
            <h3 className={`${isMobile ? 'text-sm' : 'text-base'} font-semibold text-gray-800 group-hover:text-blue-600 transition-colors truncate ${isRTL ? 'text-right' : 'text-left'}`}>
              {user.userName}
            </h3>
            <p className={`text-xs ${isMobile ? '' : 'text-sm'} text-gray-500 capitalize truncate ${isRTL ? 'text-right' : 'text-left'}`}>{userType}</p>
          </div>
        </div>
      </Link>

      {/* ==================== QUICK ACTIONS SECTION ==================== */}
      {userType && (
        <div className={`${isMobile ? 'p-3' : 'p-4'} border-b border-gray-100`}>
          <div className={`${isMobile ? 'flex flex-col space-y-2' : `flex ${isRTL ? 'space-x-reverse space-x-2' : 'space-x-2'}`}`}>
            
            {/* Notifications Button */}
            <Link
              to="/notifications"
              className={`${isMobile ? 'w-full' : 'flex-1'} flex items-center justify-center p-3 bg-gray-50 hover:bg-orange-50 rounded-lg transition-all group`}
              aria-label="View notifications"
              onClick={isMobile ? onClose : undefined}
            >
              <div className="relative">
                <svg className="w-5 h-5 text-gray-600 group-hover:text-orange-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                {/* Notification Badge */}
                {unreadNotifications > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-600 group-hover:text-orange-600 transition-colors">
                {t('notifications.title') || 'Notifications'}
              </span>
            </Link>

            {/* Chat Button */}
            <Link
              to="/chat"
              className={`${isMobile ? 'w-full' : 'flex-1'} flex items-center justify-center p-3 bg-gray-50 hover:bg-blue-50 rounded-lg transition-all group`}
              aria-label="Open chat"
              onClick={isMobile ? onClose : undefined}
            >
              <div className="relative">
                <svg className="w-5 h-5 text-gray-600 group-hover:text-blue-600 transition-colors" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                {/* Chat Badge */}
                {unreadMessages > 0 && (
                  <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </div>
              <span className="ml-2 text-sm font-medium text-gray-600 group-hover:text-blue-600 transition-colors">
                {t('chat.title') || 'Chat'}
              </span>
            </Link>
            
          </div>
        </div>
      )}

      {/* ==================== MAIN NAVIGATION ==================== */}
      <nav className={`flex-1 ${isMobile ? 'py-4 px-3' : 'py-6 px-4'} overflow-y-auto`}>
        <ul className={`${isMobile ? 'space-y-1' : 'space-y-1.5'}`}>
          
          {/* Organization and Supervisor Navigation */}
          {(userType === "organization" || userType === "supervisor") &&
            navigationItems.map((nav) => (
              <li key={nav.nameKey || nav.name}>
                <Link
                  to={nav.path}
                  className={`flex items-center ${isMobile ? 'p-2.5' : 'p-3'} hover:bg-gray-50 rounded-lg transition-all text-gray-700 hover:text-[#F15213] ${isMobile ? '' : 'hover:pl-4'} group ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}
                  aria-label={`Navigate to ${nav.nameKey ? t(nav.nameKey) : nav.name}`}
                  onClick={isMobile ? onClose : undefined}
                >
                  <div
                    className="w-5 h-5 transition-colors flex-shrink-0"
                    dangerouslySetInnerHTML={createSafeHtml(nav.icon)}
                  />
                  <span className={`${isMobile ? 'text-sm' : ''} font-medium truncate`}>{nav.nameKey ? t(nav.nameKey) : nav.name}</span>
                </Link>
              </li>
            ))}

          {/* Player Navigation */}
          {userType === "player" &&
            navigationItems.map((nav) => (
              <li key={nav.nameKey || nav.name}>
                <Link
                  to={nav.path}
                  className={`flex items-center ${isMobile ? 'p-2.5' : 'p-3'} hover:bg-gray-50 rounded-lg transition-all text-gray-700 hover:text-[#F15213] ${isMobile ? '' : 'hover:pl-4'} group ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}
                  aria-label={`Navigate to ${nav.nameKey ? t(nav.nameKey) : nav.name}`}
                  onClick={isMobile ? onClose : undefined}
                >
                  <div
                    className="w-5 h-5 transition-colors flex-shrink-0"
                    dangerouslySetInnerHTML={createSafeHtml(nav.icon)}
                  />
                  <span className={`${isMobile ? 'text-sm' : ''} font-medium truncate`}>{nav.nameKey ? t(nav.nameKey) : nav.name}</span>
                </Link>
              </li>
            ))}

          {/* Talent Scout Navigation */}
          {userType === "talent-scout" &&
            navigationItems.map((nav) => (
              <li key={nav.nameKey || nav.name}>
                <Link
                  to={nav.path}
                  className={`flex items-center ${isMobile ? 'p-2.5' : 'p-3'} hover:bg-gray-50 rounded-lg transition-all text-gray-700 hover:text-[#F15213] ${isMobile ? '' : 'hover:pl-4'} group ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}
                  aria-label={`Navigate to ${nav.nameKey ? t(nav.nameKey) : nav.name}`}
                  onClick={isMobile ? onClose : undefined}
                >
                  <div
                    className="w-5 h-5 transition-colors flex-shrink-0"
                    dangerouslySetInnerHTML={createSafeHtml(nav.icon)}
                  />
                  <span className={`${isMobile ? 'text-sm' : ''} font-medium truncate`}>{nav.nameKey ? t(nav.nameKey) : nav.name}</span>
                </Link>
              </li>
            ))}

          {/* Parent Navigation */}
          {userType === "parent" &&
            navigationItems.map((nav) => (
              <li key={nav.nameKey || nav.name}>
                {nav.isBackButton ? (
                  <button
                    onClick={() => {
                      exitControlMode();
                      if (isMobile) onClose();
                    }}
                    className={`flex items-center w-full ${isMobile ? 'p-2.5' : 'p-3'} hover:bg-gray-50 rounded-lg transition-all text-gray-700 hover:text-[#F15213] ${isMobile ? '' : 'hover:pl-4'} group ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}
                    aria-label={`Navigate to ${nav.nameKey ? t(nav.nameKey) : nav.name}`}
                  >
                    <div
                      className="w-5 h-5 transition-colors flex-shrink-0"
                      dangerouslySetInnerHTML={createSafeHtml(nav.icon)}
                    />
                    <span className={`${isMobile ? 'text-sm' : ''} font-medium truncate`}>{nav.nameKey ? t(nav.nameKey) : nav.name}</span>
                  </button>
                ) : (
                  <Link
                    to={nav.path}
                    className={`flex items-center ${isMobile ? 'p-2.5' : 'p-3'} hover:bg-gray-50 rounded-lg transition-all text-gray-700 hover:text-[#F15213] ${isMobile ? '' : 'hover:pl-4'} group ${isRTL ? 'space-x-reverse space-x-3' : 'space-x-3'}`}
                    aria-label={`Navigate to ${nav.nameKey ? t(nav.nameKey) : nav.name}`}
                    onClick={isMobile ? onClose : undefined}
                  >
                    <div
                      className="w-5 h-5 transition-colors flex-shrink-0"
                      dangerouslySetInnerHTML={createSafeHtml(nav.icon)}
                    />
                    <span className={`${isMobile ? 'text-sm' : ''} font-medium truncate`}>{nav.nameKey ? t(nav.nameKey) : nav.name}</span>
                  </Link>
                )}
              </li>
            ))}
        </ul>
      </nav>

      {/* ==================== LOGOUT SECTION ==================== */}
      <div className={`${isMobile ? 'p-3' : 'p-4'} mt-auto border-t border-gray-100`}>
        <button
          onClick={handleSignOut}
          disabled={signOutMutation.isPending}
          className={`flex items-center space-x-3 ${isMobile ? 'p-2.5' : 'p-3'} bg-gray-50 hover:bg-red-50 rounded-lg transition-all text-gray-700 hover:text-red-600 group w-full disabled:opacity-50 disabled:cursor-not-allowed`}
          aria-label="Sign out of your account"
        >
          <div className="w-5 h-5 flex-shrink-0">
            {signOutMutation.isPending ? (
              <div className="animate-spin rounded-full h-5 w-5 border-2 border-red-600 border-t-transparent"></div>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 576 512"
                className="fill-current group-hover:text-red-600 transition-colors"
              >
                <path d="M320 32c0-9.9-4.5-19.2-12.3-25.2S289.8-1.4 280.2 1l-179.9 45C79 51.3 64 70.5 64 92.5L64 448l-32 0c-17.7 0-32 14.3-32 32s14.3 32 32 32l64 0 192 0 32 0 0-32 0-448zM256 256c0 17.7-10.7 32-24 32s-24-14.3-24-32s10.7-32 24-32s24 14.3 24 32zm96-128l96 0 0 352c0 17.7 14.3 32 32 32l64 0c17.7 0 32-14.3 32-32s-14.3-32-32-32l-32 0 0-320c0-35.3-28.7-64-64-64l-96 0 0 64z" />
              </svg>
            )}
          </div>
          <span className={`${isMobile ? 'text-sm' : ''} font-medium`}>
            {signOutMutation.isPending ? t('auth.signingOut') : t('auth.signOut')}
          </span>
        </button>
      </div>
    </aside>
  );
}
